import { colors, intl, time, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { FC } from 'react'
import { SkeletonBlock } from 'room/common/presentation/skeleton'
import { useScrub } from '../use-scrub'

const Top = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
`

const Heading = styled.div`
	min-width: 0;
`

const Eyebrow = styled.div`
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: ${colors.heather};
`

const Title = styled.div<{ singleLine: boolean }>`
	font-size: 22px;
	font-weight: 700;
	color: ${colors.darkIndigo};
	word-break: break-word;

	${({ singleLine }) =>
		singleLine
			? `
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	`
			: ''}
`

const Row = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
`

const Scrubber = styled.input`
	flex: 1;
	accent-color: ${colors.indigoBlue};
`

const Time = styled.div`
	min-width: 96px;
	text-align: right;
	font-variant-numeric: tabular-nums;
	color: ${colors.darkIndigo};
`

/**
 * Play, as long as there is nothing to play: it stays where it will be and
 * says so (`aria-disabled`, not `disabled`, so it can still be reached and
 * read out together with the reason), and does nothing.
 */
const PlayNotYet = styled(ui.ButtonPrimary)`
	&[aria-disabled='true'] {
		opacity: 0.5;
		cursor: not-allowed;

		&:hover {
			transform: none;
		}
	}
`

const TrackSkeleton = styled(SkeletonBlock)`
	flex: 1;
	height: 8px;
	border-radius: 4px;
`

const TimeSkeleton = styled(SkeletonBlock)`
	width: 96px;
	height: 18px;
`

const clock = (ms: number): string =>
	time.toTimeString(Math.max(0, Math.floor(ms / 1000)))

type HeaderProps = {
	name: string
	/** One line, cut off with an ellipsis, where the height is spoken for. */
	singleLine?: boolean
	onClose: () => void
}

/** What is playing on the projector, and how to close it. */
export const RemoteHeader: FC<HeaderProps> = ({
	name,
	singleLine = false,
	onClose,
}) => (
	<Top>
		<Heading>
			<Eyebrow>
				<intl.Translate>in_person.presenter.media.on_projector</intl.Translate>
			</Eyebrow>
			<Title singleLine={singleLine} title={singleLine ? name : undefined}>
				{name}
			</Title>
		</Heading>
		<ui.ButtonClose onClick={onClose} />
	</Top>
)

type ControlsProps = {
	/** Names the slider. */
	name: string
	playing: boolean
	positionMs: number
	durationMs: number
	onPlayPause: () => void
	onSeek: (positionMs: number) => void
}

/**
 * Play or pause, a slider and the time. The player - and its sound - is in
 * the presentation window; this only sends the same commands the player's
 * own controls do and shows the position the presentation window reports. A
 * drag of the slider seeks once, when let go.
 */
export const RemoteControls: FC<ControlsProps> = ({
	name,
	playing,
	positionMs,
	durationMs,
	onPlayPause,
	onSeek,
}) => {
	const scrub = useScrub(positionMs, onSeek)
	return (
		<Row>
			<ui.ButtonPrimary
				small
				onClick={onPlayPause}
				data-testid="presenter.media_remote.play_pause"
			>
				<intl.Translate>
					{playing
						? 'in_person.presenter.media.pause'
						: 'in_person.presenter.media.play'}
				</intl.Translate>
			</ui.ButtonPrimary>
			<Scrubber
				type="range"
				min={0}
				max={Math.max(durationMs, scrub.value, 1)}
				step={1000}
				value={scrub.value}
				disabled={!durationMs}
				onChange={event => scrub.onChange(Number(event.target.value))}
				onPointerUp={scrub.onRelease}
				onKeyUp={scrub.onRelease}
				onBlur={scrub.onRelease}
				aria-label={name}
				data-testid="presenter.media_remote.scrubber"
			/>
			<Time>
				{clock(scrub.value)} / {clock(durationMs)}
			</Time>
		</Row>
	)
}

type PendingProps = {
	/** The element that says why there is nothing to play yet. */
	reasonId: string
	/** The file is not coming, rather than still on its way. */
	failed: boolean
}

/**
 * `RemoteControls` for a recording that is not there: the same row - Play,
 * where the slider goes, where the time goes - without anything to work. The
 * shapes shimmer while the file is on its way and stand still when it is not
 * coming. What follows the row's end (`children`) is for the way out.
 */
export const RemoteControlsPending: FC<PendingProps> = ({
	reasonId,
	failed,
	children,
}) => (
	<Row>
		<PlayNotYet
			small
			type="button"
			aria-disabled="true"
			aria-describedby={reasonId}
			data-testid="presenter.media_remote.play_pause"
		>
			<intl.Translate>in_person.presenter.media.play</intl.Translate>
		</PlayNotYet>
		<TrackSkeleton
			animated={!failed}
			aria-hidden="true"
			data-testid="presenter.media_remote.scrubber_skeleton"
		/>
		<TimeSkeleton animated={!failed} aria-hidden="true" />
		{children}
	</Row>
)
